import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
} from '@mui/material'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SwapHoriz as SwapHorizIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material'
import { useCustom, useNotification } from '@refinedev/core'
import { XeroReconciliationModal } from './XeroReconciliationModal'
import { ReverseReconciliationModal } from './ReverseReconciliationModal'

interface OrganisationReconciliation {
  org_uuid: string
  organisation_name: string
  payment_type: string
  billing_amount: number | null
  membership: string | null
  last_sync_attempt: Date | null
  sync_status: 'synced' | 'pending_sync' | 'sync_error' | 'manual_override'
  assigned_user_count: number
  potential_matches_count: number
}

interface XeroCustomerWithInvoices {
  contact_id: string
  contact_name: string
  email: string | null
  contact_status: string
  created_at: string
  updated_at: string
  invoice_count: number
  total_invoiced: number
  total_paid: number
  total_outstanding: number
  last_invoice_date: string | null
}

interface ReconciliationStats {
  total_needing_reconciliation: number
  in_progress: number
  completed_today: number
  with_potential_matches: number
}

interface XeroCustomersStats {
  total_with_invoices: number
  total_outstanding: number
  total_invoiced: number
  customers_with_outstanding: number
}

export const ReconciliationDashboard: React.FC = () => {
  const [organisations, setOrganisations] = useState<
    OrganisationReconciliation[]
  >([])
  const [filteredOrganisations, setFilteredOrganisations] = useState<
    OrganisationReconciliation[]
  >([])
  const [xeroCustomers, setXeroCustomers] = useState<
    XeroCustomerWithInvoices[]
  >([])
  const [filteredXeroCustomers, setFilteredXeroCustomers] = useState<
    XeroCustomerWithInvoices[]
  >([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedOrg, setSelectedOrg] =
    useState<OrganisationReconciliation | null>(null)
  const [reconciliationModalOpen, setReconciliationModalOpen] = useState(false)
  const [stats, setStats] = useState<ReconciliationStats | null>(null)
  const [xeroStats, setXeroStats] = useState<XeroCustomersStats | null>(null)
  const [viewMode, setViewMode] = useState<'organisations' | 'xero-customers'>(
    'organisations'
  )
  const [selectedCustomer, setSelectedCustomer] =
    useState<XeroCustomerWithInvoices | null>(null)
  const [reverseReconciliationModalOpen, setReverseReconciliationModalOpen] =
    useState(false)

  const { open } = useNotification()

  const { refetch: fetchOrganisations } = useCustom({
    url: '/xero/reconciliation/organisations',
    method: 'get',
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        setOrganisations(data.data)
        setFilteredOrganisations(data.data)
        calculateStats(data.data)
        setLoading(false)
      },
      onError: () => {
        open?.({
          message: 'Failed to load organisations needing reconciliation',
          type: 'error',
        })
        setLoading(false)
      },
    },
  })

  const { refetch: fetchXeroCustomers } = useCustom({
    url: '/xero/reconciliation/customers-with-invoices',
    method: 'get',
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        setXeroCustomers(data.data)
        setFilteredXeroCustomers(data.data)
        calculateXeroStats(data.data)
        setLoading(false)
      },
      onError: () => {
        open?.({
          message: 'Failed to load Xero customers with invoices',
          type: 'error',
        })
        setLoading(false)
      },
    },
  })

  const refreshData = () => {
    setLoading(true)
    if (viewMode === 'organisations') {
      fetchOrganisations()
    } else {
      fetchXeroCustomers()
    }
  }

  useEffect(() => {
    if (viewMode === 'organisations') {
      fetchOrganisations()
    } else {
      fetchXeroCustomers()
    }
  }, [viewMode, fetchOrganisations, fetchXeroCustomers])

  useEffect(() => {
    if (viewMode === 'organisations') {
      filterOrganisations()
    } else {
      filterXeroCustomers()
    }
  }, [searchTerm, statusFilter, organisations, xeroCustomers, viewMode])

  const calculateStats = (orgs: OrganisationReconciliation[]) => {
    const stats: ReconciliationStats = {
      total_needing_reconciliation: orgs.length,
      in_progress: orgs.filter((org) => org.sync_status === 'pending_sync')
        .length,
      completed_today: orgs.filter(
        (org) =>
          org.sync_status === 'synced' &&
          org.last_sync_attempt &&
          new Date(org.last_sync_attempt).toDateString() ===
            new Date().toDateString()
      ).length,
      with_potential_matches: orgs.filter(
        (org) => org.potential_matches_count > 0
      ).length,
    }
    setStats(stats)
  }

  const calculateXeroStats = (customers: XeroCustomerWithInvoices[]) => {
    const stats: XeroCustomersStats = {
      total_with_invoices: customers.length,
      total_outstanding: customers.reduce(
        (sum, customer) => sum + customer.total_outstanding,
        0
      ),
      total_invoiced: customers.reduce(
        (sum, customer) => sum + customer.total_invoiced,
        0
      ),
      customers_with_outstanding: customers.filter(
        (customer) => customer.total_outstanding > 0
      ).length,
    }
    setXeroStats(stats)
  }

  const filterOrganisations = () => {
    let filtered = organisations

    if (searchTerm) {
      filtered = filtered.filter((org) =>
        org.organisation_name.toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((org) => org.sync_status === statusFilter)
    }

    setFilteredOrganisations(filtered)
  }

  const filterXeroCustomers = () => {
    let filtered = xeroCustomers

    if (searchTerm) {
      const trimmedSearch = searchTerm.trim().toLowerCase()
      filtered = filtered.filter(
        (customer) =>
          customer.contact_name
            .toLowerCase()
            .includes(trimmedSearch) ||
          customer.email?.toLowerCase().includes(trimmedSearch)
      )
    }

    setFilteredXeroCustomers(filtered)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_sync':
      case 'sync_error':
        return 'warning'
      case 'manual_override':
        return 'info'
      case 'synced':
        return 'success'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending_sync':
        return <WarningIcon fontSize="small" />
      case 'sync_error':
        return <ErrorIcon fontSize="small" />
      case 'manual_override':
        return <RefreshIcon fontSize="small" />
      case 'synced':
        return <CheckCircleIcon fontSize="small" />
      default:
        return <ErrorIcon fontSize="small" />
    }
  }

  const handleStartReconciliation = (org: OrganisationReconciliation) => {
    setSelectedOrg(org)
    setReconciliationModalOpen(true)
  }

  const handleReconciliationComplete = () => {
    setReconciliationModalOpen(false)
    setSelectedOrg(null)
    refreshData()
  }

  const handleStartReverseReconciliation = (
    customer: XeroCustomerWithInvoices
  ) => {
    setSelectedCustomer(customer)
    setReverseReconciliationModalOpen(true)
  }

  const handleReverseReconciliationComplete = () => {
    setReverseReconciliationModalOpen(false)
    setSelectedCustomer(null)
    refreshData()
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Loading reconciliation data...
        </Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Xero Customer Reconciliation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {viewMode === 'organisations'
              ? 'Manage organisations that need Xero customer assignments.'
              : 'Review Xero customers with invoices that are not linked to any organisation.'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant={viewMode === 'organisations' ? 'contained' : 'outlined'}
            startIcon={<BusinessIcon />}
            onClick={() => setViewMode('organisations')}
          >
            Organisations Needing Reconciliation
          </Button>
          <Button
            variant={viewMode === 'xero-customers' ? 'contained' : 'outlined'}
            startIcon={<ReceiptIcon />}
            onClick={() => setViewMode('xero-customers')}
          >
            Unlinked Xero Customers
          </Button>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      {viewMode === 'organisations' && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Needing Reconciliation
                </Typography>
                <Typography variant="h4">
                  {stats.total_needing_reconciliation}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  In Progress
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.in_progress}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Completed Today
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed_today}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  With Potential Matches
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.with_potential_matches}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {viewMode === 'xero-customers' && xeroStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Customers with Invoices
                </Typography>
                <Typography variant="h4">
                  {xeroStats.total_with_invoices}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Outstanding
                </Typography>
                <Typography variant="h4" color="error.main">
                  ${xeroStats.total_outstanding.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Invoiced
                </Typography>
                <Typography variant="h4" color="primary.main">
                  ${xeroStats.total_invoiced.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  With Outstanding
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {xeroStats.customers_with_outstanding}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder={
              viewMode === 'organisations'
                ? 'Search organisations...'
                : 'Search customers...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ minWidth: 300 }}
          />

          {viewMode === 'organisations' && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending_sync">Pending Sync</MenuItem>
                <MenuItem value="sync_error">Sync Error</MenuItem>
                <MenuItem value="manual_override">Manual Override</MenuItem>
                <MenuItem value="synced">Synced</MenuItem>
              </Select>
            </FormControl>
          )}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshData}
          >
            Refresh Data
          </Button>
        </Stack>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {viewMode === 'organisations' ? (
                <>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Payment Type</TableCell>
                  <TableCell>Billing Amount</TableCell>
                  <TableCell>Membership</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Potential Matches</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Sync</TableCell>
                  <TableCell>Actions</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Invoice Count</TableCell>
                  <TableCell>Total Invoiced</TableCell>
                  <TableCell>Outstanding</TableCell>
                  <TableCell>Last Invoice</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {viewMode === 'organisations' &&
              filteredOrganisations.map((org) => (
                <TableRow key={org.org_uuid} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {org.organisation_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {org.org_uuid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={org.payment_type} />
                  </TableCell>
                  <TableCell>
                    {org.billing_amount
                      ? `$${org.billing_amount.toFixed(2)}`
                      : 'Not set'}
                  </TableCell>
                  <TableCell>{org.membership || 'Not set'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={org.assigned_user_count}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={org.potential_matches_count}
                      color={
                        org.potential_matches_count > 0 ? 'primary' : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={getStatusIcon(org.sync_status)}
                      label={org.sync_status.replace('_', ' ')}
                      color={getStatusColor(org.sync_status) as any}
                    />
                  </TableCell>
                  <TableCell>
                    {org.last_sync_attempt
                      ? new Date(org.last_sync_attempt).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Start Reconciliation">
                      <IconButton
                        size="small"
                        onClick={() => handleStartReconciliation(org)}
                        disabled={org.sync_status === 'synced'}
                      >
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

            {viewMode === 'xero-customers' &&
              filteredXeroCustomers.map((customer) => (
                <TableRow key={customer.contact_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {customer.contact_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {customer.contact_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{customer.email || 'No email'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={customer.invoice_count}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    ${customer.total_invoiced.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={
                        customer.total_outstanding > 0
                          ? 'error.main'
                          : 'text.secondary'
                      }
                      fontWeight={
                        customer.total_outstanding > 0 ? 'medium' : 'normal'
                      }
                    >
                      ${customer.total_outstanding.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {customer.last_invoice_date
                      ? new Date(
                          customer.last_invoice_date
                        ).toLocaleDateString()
                      : 'No invoices'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={customer.contact_status}
                      color={
                        customer.contact_status === 'ACTIVE'
                          ? 'success'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Link to Organisation">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleStartReverseReconciliation(customer)
                        }
                      >
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {((viewMode === 'organisations' && filteredOrganisations.length === 0) ||
        (viewMode === 'xero-customers' &&
          filteredXeroCustomers.length === 0)) &&
        !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {viewMode === 'organisations'
                ? 'No organisations found matching your criteria'
                : 'No Xero customers found matching your criteria'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewMode === 'organisations'
                ? 'Try adjusting your search or filter settings'
                : 'Try adjusting your search terms'}
            </Typography>
          </Box>
        )}

      {/* Reconciliation Modal */}
      {selectedOrg && (
        <XeroReconciliationModal
          open={reconciliationModalOpen}
          onClose={() => setReconciliationModalOpen(false)}
          organisation={selectedOrg}
          onComplete={handleReconciliationComplete}
        />
      )}

      {/* Reverse Reconciliation Modal */}
      {selectedCustomer && (
        <ReverseReconciliationModal
          open={reverseReconciliationModalOpen}
          onClose={() => setReverseReconciliationModalOpen(false)}
          xeroCustomer={selectedCustomer}
          onComplete={handleReverseReconciliationComplete}
        />
      )}
    </Box>
  )
}
